import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { toast } from 'sonner'
import { Combobox } from '@/components/common/Combobox'
import { useStages } from '@/features/admin/stages/hooks'
import { useOverrideElementStage } from '../hooks'
import type { ContractElement } from '../types'

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zmiany etapu'
}

export function OverrideElementStageDialog({ contractId, element, onClose }: {
  contractId: number
  element: ContractElement
  onClose: () => void
}) {
  const stagesQ = useStages({ kind: 'product', code: element.productCode })
  const override = useOverrideElementStage(contractId)
  const [target, setTarget] = useState<string | null>(null)

  const options = (stagesQ.data ?? [])
    .filter(s => s.code !== element.currentStageCode)
    .map(s => ({ value: s.code, label: `${s.labelPl} (${s.code})` }))

  const submit = () => {
    if (!target) return
    override.mutate(
      { elementId: element.id, targetStageCode: target },
      { onSuccess: onClose, onError: e => toast.error(formatError(e)) },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[420px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Zmień etap elementu</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            {element.productLabel} - ręczne ustawienie etapu (pomija kolejność).
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Docelowy etap</span>
              <Combobox<string>
                value={target}
                onChange={v => setTarget(v ?? null)}
                options={options}
                disabled={stagesQ.isLoading || override.isPending}
                searchThreshold={10}
              />
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={override.isPending}>Anuluj</button>
              <button type="button" onClick={submit} disabled={!target || override.isPending} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50">
                {override.isPending ? 'Zapisywanie...' : 'Zmień etap'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
